import { useState } from "react";
import { useAction } from "wasp/client/operations";
import { createWalletTransfer } from "wasp/client/operations";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../client/components/ui/select";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { format } from "date-fns";
import AddPayoutModal from "./AddPayoutModal";

interface TptAccount {
  id: string;
  name: string;
  currency: string;
  status?: string;
}

interface WalletAddModalProps {
  open: boolean;
  onClose: () => void;
  propFirmTemplateId: string;
  tptAccounts: TptAccount[];
  onMutated: () => void;
}

export function WalletAddModal({
  open,
  onClose,
  propFirmTemplateId,
  tptAccounts,
  onMutated,
}: WalletAddModalProps) {
  const t = useTranslation();
  const w = t.propFirmManager.wallet;
  const addTransfer = useAction(createWalletTransfer);

  const [tab, setTab] = useState<"transfer" | "payout">("transfer");
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  const [tAccountId, setTAccountId] = useState("");
  const [tDate, setTDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [tGross, setTGross] = useState("");
  const [tSplit, setTSplit] = useState("80");
  const [tNotes, setTNotes] = useState("");
  const [tStatus, setTStatus] = useState<"pending" | "approved">("pending");
  const [loading, setLoading] = useState(false);

  const computedNet =
    tGross && tSplit ? (parseFloat(tGross) * parseFloat(tSplit)) / 100 : 0;

  const handleAddTransfer = async () => {
    if (!tAccountId || !tDate || !tGross || !tSplit) return;
    setLoading(true);
    try {
      await addTransfer({
        propFirmTemplateId,
        accountId: tAccountId,
        date: tDate,
        grossAmount: parseFloat(tGross),
        splitPercent: parseFloat(tSplit),
        status: tStatus,
        notes: tNotes || undefined,
      });
      onMutated();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // Accounts for the payout modal need a status field
  const payoutAccounts = tptAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    status: a.status ?? "pa",
  }));

  if (showPayoutModal) {
    return (
      <AddPayoutModal
        open={showPayoutModal}
        onClose={() => {
          setShowPayoutModal(false);
          onClose();
        }}
        accounts={payoutAccounts}
        walletPropFirmTemplateId={propFirmTemplateId}
        onSuccess={() => {
          onMutated();
          setShowPayoutModal(false);
          onClose();
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{w.add}</DialogTitle>
        </DialogHeader>

        {/* Pill toggle */}
        <div className="flex gap-2 p-1 rounded-xl bg-muted/60 border border-border/50">
          <button
            type="button"
            onClick={() => setTab("transfer")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
              tab === "transfer"
                ? "bg-card text-foreground shadow-sm border border-border/60"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <ArrowDownToLine className="h-3.5 w-3.5" />
            {w.transferTab}
          </button>
          <button
            type="button"
            onClick={() => setTab("payout")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
              tab === "payout"
                ? "bg-card text-foreground shadow-sm border border-border/60"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <ArrowUpFromLine className="h-3.5 w-3.5" />
            {w.withdrawalTab}
          </button>
        </div>

        {/* Transfer tab — Compte → Wallet */}
        {tab === "transfer" && (
          <div className="space-y-3">
            <div>
              <Label>{w.accountLabel}</Label>
              <Select value={tAccountId} onValueChange={setTAccountId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tptAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{w.dateLabel}</Label>
              <Input type="date" value={tDate} onChange={(e) => setTDate(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{w.grossAmountLabel}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tGross}
                  onChange={(e) => setTGross(e.target.value)}
                  placeholder="1000"
                />
              </div>
              <div>
                <Label>{w.splitPercentLabel}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={tSplit}
                  onChange={(e) => setTSplit(e.target.value)}
                  placeholder="80"
                />
              </div>
            </div>

            {/* Net computed — highlighted */}
            {computedNet > 0 && (
              <div className="flex items-center justify-between rounded-lg bg-pnlPositive/10 border border-pnlPositive/20 px-3 py-2">
                <span className="text-xs text-muted-foreground">{w.netAmountLabel}</span>
                <span className="font-mono font-semibold text-pnlPositive">
                  ${computedNet.toFixed(2)}
                </span>
              </div>
            )}

            <div>
              <Label>{w.colStatus}</Label>
              <Select value={tStatus} onValueChange={(v) => setTStatus(v as "pending" | "approved")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{w.statusPending}</SelectItem>
                  <SelectItem value="approved">{w.statusApproved}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{w.notesLabel}</Label>
              <Input
                value={tNotes}
                onChange={(e) => setTNotes(e.target.value)}
                placeholder={w.notesPlaceholder}
              />
            </div>
            <Button
              className="w-full"
              onClick={handleAddTransfer}
              disabled={loading || !tAccountId || !tGross}
            >
              {w.add}
            </Button>
          </div>
        )}

        {/* Payout tab — Wallet → Externe : ouvre AddPayoutModal */}
        {tab === "payout" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {w.withdrawalTabDesc}
            </p>
            <Button
              className="w-full"
              onClick={() => setShowPayoutModal(true)}
            >
              <ArrowUpFromLine className="h-4 w-4 mr-2" />
              {t.propFirmManager.addPayoutBtn}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
